import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AiCategorySuggestionStats } from "./types";

export function AiCategorySuggestionsPanel() {
  const statsQuery = useQuery<AiCategorySuggestionStats>({
    queryKey: ["/api/admin/expense-ai-suggestion-stats"],
    queryFn: async () => {
      const res = await fetch("/api/admin/expense-ai-suggestion-stats", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load AI suggestion stats");
      return res.json();
    },
  });
  const data = statsQuery.data;
  const totalAcceptRate = data && data.totals.total > 0
    ? Math.round((data.totals.accepted / data.totals.total) * 100)
    : null;
  return (
    <div className="space-y-4" data-testid="panel-ai-category-suggestions">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-ai-suggestions-title">AI Category Suggestions</h3>
          <p className="text-sm text-muted-foreground">
            Telemetry on the violet "AI" pill in Financials → Expenses. Tracks whether hosts apply suggestions or override them.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => statsQuery.refetch()}
          disabled={statsQuery.isFetching}
          data-testid="button-refresh-ai-suggestion-stats"
        >
          {statsQuery.isFetching ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Refresh
        </Button>
      </div>
      {statsQuery.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : statsQuery.isError ? (
        <Card className="p-6 text-sm text-destructive" data-testid="text-ai-suggestion-stats-error">
          Failed to load stats.
        </Card>
      ) : !data || data.totals.total === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="text-ai-suggestion-stats-empty">
          No suggestion events recorded yet. Hosts haven't applied or overridden any AI category pills.
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Total events</div>
              <div className="text-2xl font-semibold" data-testid="stat-ai-total">{data.totals.total}</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Accepted</div>
              <div className="text-2xl font-semibold text-emerald-700" data-testid="stat-ai-accepted">{data.totals.accepted}</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Overridden</div>
              <div className="text-2xl font-semibold text-amber-700" data-testid="stat-ai-overridden">{data.totals.overridden}</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Accept rate</div>
              <div className="text-2xl font-semibold" data-testid="stat-ai-accept-rate">{totalAcceptRate}%</div>
            </Card>
          </div>

          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-2">Accept rate per suggested category</h4>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Suggested category</TableHead>
                    <TableHead className="text-right">Accepted</TableHead>
                    <TableHead className="text-right">Overridden</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Accept rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.byCategory.map((row) => {
                    const rate = row.total > 0 ? Math.round((row.accepted / row.total) * 100) : 0;
                    return (
                      <TableRow key={row.suggestedCategory} data-testid={`row-ai-category-${row.suggestedCategory}`}>
                        <TableCell className="font-medium">{row.suggestedCategory}</TableCell>
                        <TableCell className="text-right">{row.accepted}</TableCell>
                        <TableCell className="text-right">{row.overridden}</TableCell>
                        <TableCell className="text-right">{row.total}</TableCell>
                        <TableCell className="text-right" data-testid={`text-ai-accept-rate-${row.suggestedCategory}`}>{rate}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-2">Top overridden vendors</h4>
            {data.topOverriddenVendors.length === 0 ? (
              <div className="text-sm text-muted-foreground">No vendor overrides yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor</TableHead>
                      <TableHead>AI suggested</TableHead>
                      <TableHead>Hosts most often chose</TableHead>
                      <TableHead className="text-right">Overrides</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.topOverriddenVendors.map((row) => (
                      <TableRow key={`${row.vendorNormalized}::${row.suggestedCategory}`} data-testid={`row-ai-override-vendor-${row.vendorNormalized}`}>
                        <TableCell className="font-medium">{row.vendorNormalized}</TableCell>
                        <TableCell>{row.suggestedCategory}</TableCell>
                        <TableCell>{row.topChosenCategory ?? "—"}</TableCell>
                        <TableCell className="text-right">{row.overrideCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
